import { View, ViewOpts } from '@play-co/timestep-core/ui';
import { MagicLampsLamp } from './MagicLampsLamp';
import { MagicLampsPrize } from 'src/replicant/match2/magiclamps/magiclamps.ruleset';

interface MagicLampsLampSetOpts extends ViewOpts {
  onPick?: (index: number) => Promise<void>;
  amount: number;
}

export class MagicLampsLampSet extends View {
  public get length() {
    return this.lamps.length;
  }
  private pickEnabled = false;
  private lamps: MagicLampsLamp[] = [];
  private genie?: View;

  constructor(opts: MagicLampsLampSetOpts) {
    super({ clipEvents: false, ...opts });

    const spacing = 142;
    const offsetX = (opts.amount - 1) * spacing * 0.5;

    for (let i = 0; i < opts.amount; i++) {
      const lamp = new MagicLampsLamp({
        superview: this,
        x: i * spacing - offsetX,
        onSelect: () => this.pickEnabled && opts.onPick?.(i),
      });
      this.lamps.push(lamp);
    }
  }

  public setStage(stage: number) {
    this.lamps.forEach((lamp) => lamp.setStage(stage));
  }

  public async lampsSetPrizes(selectedLamp: number, prizes: MagicLampsPrize[]) {
    const selected = this.lamps[selectedLamp];
    const others = this.lamps.filter(
      (lamp, i) => i !== selectedLamp && !lamp.out,
    );

    // First prize is the selected one
    selected.setPrize(prizes.shift());

    // Set remaining prizes to other lamps
    others.forEach((lamp) => {
      lamp.setPrize(prizes.shift());
    });
  }

  public async lampsReveal(selectedLamp: number) {
    if (this.lamps[selectedLamp].trap) {
      await this.lamps[selectedLamp].open();
      return;
    }

    await Promise.all(this.lamps.map((lamp) => lamp.open()));
  }

  public async lampsReset() {
    await Promise.all(this.lamps.map((lamp) => lamp.reset()));
  }

  public async lampsJumpOut() {
    await Promise.all(this.lamps.map((lamp) => lamp.jumpOut()));
  }

  public async lampsJumpIn() {
    this.updateOpts({ visible: true });
    await Promise.all(this.lamps.map((lamp) => lamp.jumpIn()));
  }

  public async highlighLamp(index: number) {
    await Promise.all(
      this.lamps.map((lamp, i) => {
        if (i === index) {
          return lamp.bringToFront();
        } else {
          return lamp.sendToBackground();
        }
      }),
    );
  }

  public async clearHighlight() {
    await Promise.all(
      this.lamps.map((lamp) => {
        lamp.backToIdle();
      }),
    );
  }

  public async removeTrap() {
    await Promise.all(this.lamps.map((lamp) => lamp.reset()));
    const trapLamp = this.lamps.find((lamp) => lamp.trap);
    if (trapLamp) await trapLamp.jumpOut();
  }

  public enablePick() {
    this.pickEnabled = true;
  }

  public disablePick() {
    this.pickEnabled = false;
  }

  public getLamp(index: number) {
    return this.lamps[index];
  }
}
